import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { doc, updateDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../utils/firebase';
import { useNavigate } from 'react-router-dom';
import { Student } from '../../../utils/firebase';

interface FormData {
  name: string;
  photoURL: string;
}

const StudentSettings: React.FC = () => {
  const { currentUser, setCurrentUser, logout } = useAuth();
  const navigate = useNavigate();
  const student = currentUser as Student;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    photoURL: '',
  });

  useEffect(() => {
    if (student) {
      setFormData({
        name: student.name,
        photoURL: student.photoURL || '',
      });
    }
  }, [student]);

  const handleProfileImageUpload = async (file: File) => {
    if (!student) return;

    try {
      if (!storage) {
        throw new Error('Storage service is not available');
      }
      
      setUploading(true);
      const storageRef = ref(storage, `profiles/${student.uid}/${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      
      const studentRef = doc(db, `schools/${student.schoolId}/students/${student.uid}`);
      await updateDoc(studentRef, {
        photoURL: downloadUrl,
        updatedAt: serverTimestamp()
      });

      setCurrentUser((prev: any) => {
        if (!prev) return prev;
        return {
          ...prev,
          photoURL: downloadUrl
        };
      });
      
      setFormData({ ...formData, photoURL: downloadUrl });
      setSuccess('프로필 이미지가 업로드되었습니다.');
    } catch (error) {
      console.error('Error uploading profile image:', error);
      setError('프로필 이미지 업로드 중 오류가 발생했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!student?.uid || !student?.schoolId) return;

    try {
      setLoading(true);
      const studentDocRef = doc(db, `schools/${student.schoolId}/students/${student.uid}`);
      
      // 업데이트할 데이터 준비
      const updatedData: any = {
        name: formData.name,
        updatedAt: serverTimestamp()
      };

      // undefined 값 제거
      Object.keys(updatedData).forEach(key => {
        if (updatedData[key] === undefined) {
          delete updatedData[key];
        }
      });

      await updateDoc(studentDocRef, updatedData);
      
      // 현재 사용자 정보 업데이트
      setCurrentUser((prev: any) => {
        if (!prev || prev.role !== 'student') return prev;
        return {
          ...prev,
          ...updatedData
        };
      });
      
      setSuccess('프로필이 성공적으로 업데이트되었습니다.');
    } catch (err) {
      console.error('프로필 업데이트 중 오류:', err);
      setError('프로필 업데이트에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError('');

      await deleteDoc(doc(db, 'users', currentUser.uid));
      await logout();
      navigate('/');
    } catch (error: any) {
      console.error('Error deleting account:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ fontSize: '16px', color: 'var(--Color-text-normal, #161616)' }}>로딩 중...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '8px' }}>
      <div style={{ marginBottom: '16px' }}>
        <h1
          style={{
            margin: 0,
            color: 'var(--Color-text-normal, #161616)',
            fontFamily: 'Pretendard, sans-serif',
            fontSize: '32px',
            fontWeight: 600,
            lineHeight: '150%',
          }}
        >
          설정
        </h1>
      </div>

      {error && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px 16px',
            borderRadius: '8px',
            border: '1px solid #f5c2c7',
            backgroundColor: '#f8d7da',
            color: '#842029',
            fontSize: '14px',
            fontFamily: 'Pretendard, sans-serif',
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px 16px',
            borderRadius: '8px',
            border: '1px solid #badbcc',
            backgroundColor: '#d1e7dd',
            color: '#0f5132',
            fontSize: '14px',
            fontFamily: 'Pretendard, sans-serif',
          }}
        >
          {success}
        </div>
      )}

      <div
        style={{
          padding: '24px',
          marginBottom: '24px',
          borderRadius: '12px',
          backgroundColor: 'var(--Color-background-normal, white)',
          border: '1px solid var(--Color-line-normal, #F0F0F0)',
        }}
      >
        <h2
          style={{
            margin: '0 0 8px 0',
            color: 'var(--Color-text-normal, #161616)',
            fontFamily: 'Pretendard, sans-serif',
            fontSize: '20px',
            fontWeight: 600,
            lineHeight: '150%',
          }}
        >
          프로필 정보
        </h2>
        <p
          style={{
            margin: '0 0 16px 0',
            color: 'var(--Color-text-assistive, #989898)',
            fontFamily: 'Pretendard, sans-serif',
            fontSize: '14px',
            lineHeight: '150%',
          }}
        >
          기본 정보를 확인하고 수정할 수 있습니다.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
            <img
              src={formData.photoURL || '/images/icons/Profile.svg'}
              alt="프로필 이미지"
              style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '1px solid var(--Color-line-normal, #F0F0F0)',
                backgroundColor: 'var(--Color-background-sub, #F8F8F8)',
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ fontSize: '24px', fontWeight: 600, color: 'var(--Color-text-normal, #161616)' }}>{formData.name}</div>
              <div style={{ color: 'var(--Color-text-assistive, #989898)' }}>{currentUser?.email || ''}</div>
              <div style={{ color: 'var(--Color-text-assistive, #989898)' }}>학생</div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '14px', color: 'var(--Color-text-assistive, #989898)' }}>이름</label>
            <input
              value={formData.name}
              disabled
              style={{
                width: '100%',
                boxSizing: 'border-box',
                height: '44px',
                borderRadius: '8px',
                border: '1px solid var(--Color-line-normal, #F0F0F0)',
                padding: '0 12px',
                backgroundColor: 'var(--Color-background-sub, #F8F8F8)',
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '14px', color: 'var(--Color-text-assistive, #989898)' }}>이메일</label>
            <input
              value={currentUser?.email || ''}
              disabled
              style={{
                width: '100%',
                boxSizing: 'border-box',
                height: '44px',
                borderRadius: '8px',
                border: '1px solid var(--Color-line-normal, #F0F0F0)',
                padding: '0 12px',
                backgroundColor: 'var(--Color-background-sub, #F8F8F8)',
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '14px', color: 'var(--Color-text-assistive, #989898)' }}>학급</label>
            <input
              value={`${student?.grade || ''}학년 ${student?.class || ''}반`}
              disabled
              style={{
                width: '100%',
                boxSizing: 'border-box',
                height: '44px',
                borderRadius: '8px',
                border: '1px solid var(--Color-line-normal, #F0F0F0)',
                padding: '0 12px',
                backgroundColor: 'var(--Color-background-sub, #F8F8F8)',
              }}
            />
          </div>
        </div>
      </div>

      <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          style={{
            height: '44px',
            padding: '0 20px',
            borderRadius: '10px',
            border: 'none',
            backgroundColor: 'var(--Color-fill-brand-color, #FF6F0F)',
            color: 'white',
            fontSize: '14px',
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? '저장 중...' : '설정 저장'}
        </button>
      </div>

      <div
        style={{
          padding: '24px',
          marginTop: '24px',
          borderRadius: '12px',
          backgroundColor: 'var(--Color-background-normal, white)',
          border: '1px solid var(--Color-line-normal, #F0F0F0)',
        }}
      >
        <h2
          style={{
            margin: '0 0 8px 0',
            color: 'var(--Color-status-negative, #FF3838)',
            fontFamily: 'Pretendard, sans-serif',
            fontSize: '20px',
            fontWeight: 600,
            lineHeight: '150%',
          }}
        >
          회원 탈퇴
        </h2>
        <p
          style={{
            margin: '0 0 16px 0',
            color: 'var(--Color-text-assistive, #989898)',
            fontFamily: 'Pretendard, sans-serif',
            fontSize: '14px',
            lineHeight: '150%',
          }}
        >
          계정을 삭제하면 모든 데이터가 영구적으로 삭제됩니다.
        </p>
        <button
          type="button"
          onClick={() => setShowDeleteConfirm(true)}
          style={{
            height: '40px',
            padding: '0 16px',
            borderRadius: '8px',
            border: '1px solid var(--Color-status-negative, #FF3838)',
            backgroundColor: 'transparent',
            color: 'var(--Color-status-negative, #FF3838)',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          회원 탈퇴
        </button>
      </div>

      {showDeleteConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px',
          }}
        >
          <div
            style={{
              padding: '24px',
              maxWidth: '400px',
              width: '100%',
              borderRadius: '12px',
              backgroundColor: 'var(--Color-background-normal, white)',
              border: '1px solid var(--Color-line-normal, #F0F0F0)',
            }}
          >
            <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 600, color: 'var(--Color-text-normal, #161616)' }}>
              회원 탈퇴 확인
            </h3>
            <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: 'var(--Color-text-assistive, #989898)', lineHeight: '150%' }}>
              정말로 탈퇴하시겠습니까? 이 작업은 되돌릴 수 없습니다.
            </p>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  height: '40px',
                  padding: '0 16px',
                  borderRadius: '8px',
                  border: '1px solid var(--Color-line-normal, #F0F0F0)',
                  backgroundColor: 'white',
                  color: 'var(--Color-text-normal, #161616)',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                style={{
                  height: '40px',
                  padding: '0 16px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: 'var(--Color-status-negative, #FF3838)',
                  color: 'white',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                탈퇴하기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentSettings; 